import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Inbox,
  FileText,
  Newspaper,
  HelpCircle,
  MessageSquareQuote,
  Image,
  Wrench,
  Tags,
  Users,
  Settings,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { SiteLogo } from '../ui/SiteLogo';
import './AdminSidebar.css';

const STORAGE_KEY = 'admin-sidebar-groups';

const LIENS_HAUT = [
  { to: '/admin', label: 'Tableau de bord', icon: LayoutDashboard, permission: null, end: true },
  { to: '/admin/leads', label: 'Demandes', icon: Inbox, permission: 'leads.manage' },
];

const GROUPES = [
  {
    id: 'catalogue',
    label: 'Catalogue',
    liens: [
      { to: '/admin/services', label: 'Services', icon: Wrench, permission: 'services.manage' },
      { to: '/admin/categories', label: 'Catégories', icon: Tags, permission: 'categories.manage' },
    ],
  },
  {
    id: 'contenu',
    label: 'Contenu',
    liens: [
      { to: '/admin/pages', label: 'Pages', icon: FileText, permission: 'pages.manage' },
      { to: '/admin/articles', label: 'Actualités & réalisations', icon: Newspaper, permission: 'articles.manage' },
      { to: '/admin/faqs', label: 'FAQ', icon: HelpCircle, permission: 'faqs.manage' },
      { to: '/admin/temoignages', label: 'Témoignages', icon: MessageSquareQuote, permission: 'temoignages.manage' },
      { to: '/admin/media', label: 'Médiathèque', icon: Image, permission: 'media.manage' },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    liens: [
      { to: '/admin/utilisateurs', label: 'Utilisateurs', icon: Users, permission: 'users.manage' },
      { to: '/admin/parametres', label: 'Paramètres', icon: Settings, permission: 'settings.manage' },
    ],
  },
];

function chargerGroupeOuvert() {
  return localStorage.getItem(STORAGE_KEY) || null;
}

export function AdminSidebar() {
  const { can } = useAuth();
  const location = useLocation();
  // Un seul groupe ouvert à la fois (accordéon) : on stocke son id, ou null si tous fermés.
  const [groupeOuvert, setGroupeOuvert] = useState(chargerGroupeOuvert);

  // Ouvre automatiquement le groupe contenant la page active (première visite / navigation directe),
  // en fermant les autres.
  useEffect(() => {
    const groupeActif = GROUPES.find((g) => g.liens.some((l) => location.pathname.startsWith(l.to)));
    if (groupeActif) {
      setGroupeOuvert(groupeActif.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  function toggleGroupe(id) {
    setGroupeOuvert((prev) => {
      const next = prev === id ? null : id;
      localStorage.setItem(STORAGE_KEY, next || '');
      return next;
    });
  }

  const groupesVisibles = GROUPES.map((g) => ({
    ...g,
    liens: g.liens.filter((l) => !l.permission || can(l.permission)),
  })).filter((g) => g.liens.length > 0);

  return (
    <aside className="admin-sidebar">
      <div className="admin-sidebar__brand">
        <SiteLogo tone="sombre" height={34} />
      </div>

      <nav>
        {LIENS_HAUT.filter((l) => !l.permission || can(l.permission)).map((lien) => (
          <NavLink key={lien.to} to={lien.to} end={lien.end} className="admin-sidebar__link">
            <lien.icon size={17} />
            {lien.label}
          </NavLink>
        ))}
      </nav>

      {groupesVisibles.map((groupe) => {
        const estOuvert = groupeOuvert === groupe.id;
        return (
          <div className="admin-sidebar__group" key={groupe.id}>
            <button
              type="button"
              className="admin-sidebar__group-header"
              onClick={() => toggleGroupe(groupe.id)}
              aria-expanded={estOuvert}
            >
              <span>{groupe.label}</span>
              <ChevronDown size={15} className={`admin-sidebar__chevron ${estOuvert ? 'admin-sidebar__chevron--open' : ''}`} />
            </button>

            {estOuvert && (
              <nav>
                {groupe.liens.map((lien) => (
                  <NavLink key={lien.to} to={lien.to} className="admin-sidebar__link">
                    <lien.icon size={17} />
                    {lien.label}
                  </NavLink>
                ))}
              </nav>
            )}
          </div>
        );
      })}
    </aside>
  );
}
